<template>
  <v-container>
    <v-row>
      <v-col v-if="response != null" cols="12" md="9">
        <v-card class="pa-4">
          <v-row>
            <v-col cols="12" md="4">
              <v-simple-table dense class="stripped font-weight-light mb-4">
                <thead>
                  <tr>
                    <th>{{ $t("realtimeMap.Time") }}</th>
                    <th>{{ $t("reportHeaders.Total Time") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ $t("timeSpentOnDriving") }}</td>
                    <td>{{ selectedDriver != null ? selectedDriver.totalTimeSpentOnDriving : "" }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t("timeSpentOnPause") }}</td>
                    <td>{{ selectedDriver != null ? selectedDriver.totalTimeSpentOnPause : "" }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
            <v-col v-if="selectedDriver != null" cols="12" md="8" class="d-flex align-center">
              <v-alert type="info" class="mb-4" outlined>
                {{ $t("startIntervalHasBeenMoved") }}
                <strong>{{ selectedDriver.checkInterval }}</strong>
              </v-alert>
            </v-col>
          </v-row>

          <v-tabs v-model="activeTab" class="py-4" background-color="primary">
            <v-tab>{{ $t("drivers") }}</v-tab>
            <v-tab>{{ $t("results") }}</v-tab>
            <v-tab>{{ $t("violations") }}</v-tab>
            <template v-slot:extension>
              <span v-if="selectedDriver" class="ml-4 text-h6">Selected: <strong>{{ selectedDriver.text }}</strong></span>
            </template>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <div class="local-table">
                <v-data-table
                  :headers="driverHeaders"
                  :items="drivers"
                  dense
                  class="font-weight-light"
                  hide-default-footer
                  @click:row="onDriverClick"
                  mobile-breakpoint
                ></v-data-table>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="local-table results-data-container">
                <v-data-table
                  :headers="resultsHeaders"
                  :items="resultsData"
                  class="elevation-1"
                  dense
                  hide-default-footer
                  :items-per-page="resultsData.length"
                  :item-class="applyDrivingStatusClass"
                ></v-data-table>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="local-table">
                <v-data-table
                  :headers="violationsHeaders"
                  :items="violationsData"
                  class="stripped elevation-1"
                  :item-class="applyIsMinorViolationClass"
                  dense
                  hide-default-footer
                ></v-data-table>
              </div>
            </v-tab-item>
          </v-tabs-items>

          <div class="my-5">
            <v-btn color="success" :disabled="response == null" small @click="exportAsExcel" class="mr-2 font-weight-light">Excel</v-btn>
            <v-btn color="success" :disabled="response == null" small @click="exportAsPdf" class="mr-2 font-weight-light">PDF</v-btn>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="3" :offset-md="response == null ? 9 : 0">
        <v-card class="px-4 py-2">
          <v-card-text>
            <h4 class="font-weight-light pt-3">{{ $t('mapFilters.Options') }} </h4>

            <ValidationObserver ref="form" v-slot="{ handleSubmit }">
              <form @submit.prevent="handleSubmit(fetchFilteredData)">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="Drivers"
                  rules="required"
                >
                  <VSelectAll
                    v-model="searchObject.selectedDrivers"
                    :items="allDrivers"
                    item-text="name"
                    item-value="id"
                    multiple
                    :label="$t('drivers')"
                    outlined
                    dense
                    :error-messages="errors"
                    hide-details="auto"
                  ></VSelectAll>
                </ValidationProvider>

                <ValidationProvider
                  v-slot="{ validate, errors }"
                  :name="'date'"
                  vid="date"
                  rules="required"
                >
                  <v-date-field
                    color="primary"
                    dense
                    hide-details="auto"
                    outlined
                    v-model="searchObject.selectedDate"
                    @input="setSelectedDate"
                    :label="$t('reportHeaders.Date From')"
                    id="date"
                    :error-messages="errors"
                  ></v-date-field>
                </ValidationProvider>

                <ValidationProvider
                  v-slot="{ errors }"
                  name="Period"
                  rules="required"
                >
                  <v-autocomplete
                    v-model="searchObject.selectedPeriodForCheck"
                    :items="periods"
                    :label="$t('periodForCheck')"
                    outlined
                    dense
                    :error-messages="errors"
                    hide-details="auto"
                  ></v-autocomplete>
                </ValidationProvider>

                <div class="text-right my-4">
                  <v-btn color="primary" small type="submit" :loading="loadingRequest" :disabled="loadingRequest">
                    {{ $t('search') }}
                  </v-btn>
                </div>
              </form>
            </ValidationObserver>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import httpClient from '../api/httpClient'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import formatDate from '@/utils/formatDateHelper.js'
const Excel = require('exceljs')
import JSZip from 'jszip'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { checkIfDateIsAlreadyFormatted } from '../utils/dateHelper'
import { replaceSerbianChars } from '../utils/pdfHelper'

export default {
  name: "ZobsPage",
  components: {
    ValidationObserver,
    ValidationProvider
  },
  data() {
    return {
      activeTab: 0,
      searchObject: {
        selectedDrivers: [],
        selectedDate: null,
        selectedPeriodForCheck: null
      },
      selectedDriver: null,
      allDrivers: [],
      drivers: [],
      periods: [
        { text: this.$t("last24Hours"), value: 1},
        { text: this.$t("last7Days"), value: 2},
        { text: this.$t("last14Days"), value: 3}
      ],
      driverHeaders: [
        { text: this.$t('driver'), value: 'driverName' },
        { text: this.$t('hasViolations'), value: 'hasViolations' }
      ],
      resultsHeaders: [
        { text: this.$t('startDate'), value: 'startDate' },
        { text: this.$t('endDate'), value: 'endDate' },
        { text: this.$t('type'), value: 'drivingStatus' },
        { text: this.$t('reportHeaders.Duration'), value: 'duration' }
      ],
      resultsData: [],
      violationsHeaders: [
        { text: this.$t('reportHeaders.Date'), value: 'timestamp' },
        { text: this.$t('violation'), value: 'violation' }
      ],
      violationsData: [],
      loadingRequest: false,
      response: null
    }
  },
  async mounted() {
    await this.loadDrivers()
  },
  methods: {
    async loadDrivers() {
      await httpClient.get('/drivers').then(response => {
        for (let data of response.data) {
          this.allDrivers.push({ text: data.name, value: data.id })
        }
      })
    },
    async fetchFilteredData() {
      this.loadingRequest = true

      var url = "/reports/GetZobsReport?";
      for (var driverId of this.searchObject.selectedDrivers) {
        url += `DriverIds=${driverId}&`
      }

      url += `FromDate=${this.searchObject.selectedDate}&PeriodForCheck=${this.searchObject.selectedPeriodForCheck}`

      await httpClient.get(url)
        .then(response => {
          this.response = response.data

          this.drivers = response.data.drivers
          this.activeTab = 0

          this.formatTimestamps(this.drivers)
        })

      this.loadingRequest = false
    },
    onDriverClick(driver) {
      if (driver.results.length) {
        this.selectedDriver = driver
        this.resultsData = this.selectedDriver.results

        this.violationsData = this.selectedDriver.violations
        this.activeTab = 1
      }
    },
    setSelectedDate(value) {
      this.selectedDate = value
    },
    applyIsMinorViolationClass(item) {
      if (item.isMinorViolation) {
        return 'orange--text'
      }

      return 'red--text'
    },
    applyDrivingStatusClass(item) {
      if (item.drivingStatus == "Vožnja") {
        return 'green-row'
      }

      return 'red-row'
    },
    formatTimestamps(drivers) {
      for (const driver of drivers) {
        for (const result of driver.results) {
          if (checkIfDateIsAlreadyFormatted(result.startDate)) {
            continue
          }

          result.startDate = formatDate(result.startDate, 'long', true)
          result.endDate = formatDate(result.endDate, 'long', true)
        }
      }
    },
    async generateResultsExcel() {
      const workbook = new Excel.Workbook()
      workbook.creator = 'Puregreen'
      workbook.lastModifiedBy = 'Puregreen'

      for (const driver of this.response.drivers) {
        const worksheet = workbook.addWorksheet(driver.driverName)

        worksheet.addRow([`Vozač`, `${driver.driverName}`])
        const selectedDate = new Date(this.searchObject.selectedDate)
        worksheet.addRow([`Datum od`, `${selectedDate.getDate()}.${selectedDate.getMonth() + 1}.${selectedDate.getFullYear()} 00:00:00`])
        let periodForCheck = this.periods.find(x => x.value == this.searchObject.selectedPeriodForCheck).text
        worksheet.addRow([`Period za proveru`, periodForCheck])
        worksheet.addRow([this.$t("startIntervalHasBeenMoved"), driver.checkInterval])
        worksheet.addRow([`Ukupno vremena provedeno u vožnji (hh:mm:ss)`, driver.totalTimeSpentOnDriving])
        worksheet.addRow([`Ukupno vremena provedeno u pauzi (hh:mm:ss)`, driver.totalTimeSpentOnPause])
        worksheet.addRow([])

        worksheet.addRow([this.$t('startDate'), this.$t("endDate"), this.$t('type'), this.$t('reportHeaders.Duration'), "Status"])

        worksheet.columns = [
          { width: 30 }, 
          { width: 30 },
          { width: 30 },
          { width: 30 }, 
          { width: 30 }, 
        ];

        for (const result of driver.results) {
          const pauseStatuses = ["Pauza 15 min", "Pauza 30 min", "Pauza 45 min", "Dnevni odmor", "Nedeljni odmor", "Pauza kraća od 30 min"]
          const status = pauseStatuses.some(x => result.drivingStatus.includes(x)) ? "PAUZA" : ""

          let startDate = result.startDate
          let endDate = result.endDate

          const row = worksheet.addRow([
            startDate,
            endDate,
            result.drivingStatus,
            result.duration,
            status,
          ])

          if (result.drivingStatus == "Vožnja") {
            row.eachCell((cell) => {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "A5D6A7" },
              }
            })
          } else {
            row.eachCell((cell) => {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "EF9A9A" },
              }
            })
          }

        }
      }

      const buffer = await workbook.xlsx.writeBuffer()
      return new Blob([buffer], { type: "application/octet-stream" })
    },
    async generateViolationsExcel() {
      var workbook = new Excel.Workbook()
      workbook.creator = 'Puregreen'
      workbook.lastModifiedBy = 'Puregreen'

      for (const driver of this.response.drivers) {
        const worksheet = workbook.addWorksheet(driver.driverName)

        worksheet.addRow([`Vozač`, `${driver.driverName}`])
        const selectedDate = new Date(this.searchObject.selectedDate)
        worksheet.addRow([`Datum od`, `${selectedDate.getDate()}.${selectedDate.getMonth() + 1}.${selectedDate.getFullYear()} 00:00:00`])
        let periodForCheck = this.periods.find(x => x.value == this.searchObject.selectedPeriodForCheck).text
        worksheet.addRow([`Period za proveru`, periodForCheck])
        worksheet.addRow([`Ukupno vremena provedeno u vožnji (hh:mm:ss)`, driver.totalTimeSpentOnDriving])
        worksheet.addRow([`Ukupno vremena provedeno u pauzi (hh:mm:ss)`, driver.totalTimeSpentOnPause])
        worksheet.addRow([])

        worksheet.addRow([this.$t('reportHeaders.Date'), this.$t('violation')])

        worksheet.columns = [
          { width: 30 }, 
          { width: 30 },
        ];

        for (const violation of driver.violations) {
          const row = worksheet.addRow([
            violation.timestamp,
            violation.violation,
          ]);

          row.eachCell((cell) => {
            cell.font = {
              color: { argb: violation.isMinorViolation ? "FFA500" : "FF0000" },
            }
          })
        }
      }

      const buffer = await workbook.xlsx.writeBuffer()
      return new Blob([buffer], { type: "application/octet-stream" })
    },
    async exportAsExcel() {
      const zip = new JSZip()

      const resultsBlob = await this.generateResultsExcel()
      const violationsBlob = await this.generateViolationsExcel()

      zip.file("Rezultati.xlsx", resultsBlob)
      zip.file("Prekrsaji.xlsx", violationsBlob)

      const zipBlob = await zip.generateAsync({ type: "blob" })

      var currentDate = new Date()
      var zipFileName = `ZOBS-${currentDate.getDate()}.${currentDate.getMonth() + 1}.${currentDate.getFullYear()} ${currentDate.getHours()}_${currentDate.getMinutes()}_${currentDate.getSeconds()}.zip`

      const url = URL.createObjectURL(zipBlob)
      const a = document.createElement("a")
      a.href = url
      a.download = zipFileName
      a.style.display = "none";

      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    async generateResultsPDF() {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      })

      doc.setFontSize(10)

      this.response.drivers.forEach((driver, driverIndex) => {
        let currentY = 10

        doc.text(`${replaceSerbianChars(this.$t('tables.Driver'))}: ${replaceSerbianChars(driver.driverName)}`, 10, currentY)
        currentY += 8
        const selectedDate = new Date(this.searchObject.selectedDate)
        doc.text(
          `${this.$t('reportHeaders.Date From')}: ${selectedDate.getDate()}.${selectedDate.getMonth() + 1}.${selectedDate.getFullYear()} 00:00:00`,
          10,
          currentY
        )
        currentY += 8
        const periodForCheck = this.periods.find(
          (x) => x.value === this.searchObject.selectedPeriodForCheck
        ).text
        doc.text(
          `${this.$t('periodForCheck')}: ${replaceSerbianChars(periodForCheck)}`,
          10,
          currentY
        )

        currentY += 8
        doc.text(
          `${replaceSerbianChars(this.$t('startIntervalHasBeenMoved'))}: ${driver.checkInterval}`,
          10,
          currentY
        )

        currentY += 8
        doc.text(
          `${this.$t('timeSpentOnDriving')}: ${driver.totalTimeSpentOnDriving}`,
          10,
          currentY
        )
        currentY += 8
        doc.text(
          `${this.$t('timeSpentOnPause')}: ${driver.totalTimeSpentOnPause}`,
          10,
          currentY
        )
        currentY += 10

        const headers = [
          this.$t("startDate"),
          this.$t("endDate"),
          this.$t("type"),
          this.$t("reportHeaders.Duration"),
          "Status",
        ]

        doc.autoTable({
          startY: currentY,
          head: [headers.map(replaceSerbianChars)],
          body: driver.results.map((result) => {
            const pauseStatuses = [
              "Pauza 15 min", "Pauza 30 min", "Pauza 45 min",
              "Dnevni odmor", "Nedeljni odmor", "Pauza kraca od 30 min",
            ]
            const status = pauseStatuses.some((x) => result.drivingStatus.includes(x))
              ? "PAUZA"
              : ""

            let startDate = result.startDate
            let endDate = result.endDate

            return [
              startDate,
              endDate,
              replaceSerbianChars(result.drivingStatus),
              result.duration,
              status,
            ]
          }),
          styles: {
            font: "helvetica",
            fontSize: 10,
            halign: "center",
            textColor: [0, 0, 0],
          },
          bodyStyles: {
            fillColor: [239, 154, 154],
            textColor: [0, 0, 0],
          },
          alternateRowStyles: {
            fillColor: [165, 214, 167],
            textColor: [0, 0, 0],
          },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: [0, 0, 0],
          },
          margin: { top: 10 },
        })

        if (driverIndex < this.response.drivers.length - 1) {
          doc.addPage()
        }
      })

      return doc.output("blob")
    },
    async generateViolationsPDF() {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      })

      doc.setFontSize(10)

      this.response.drivers.forEach((driver, driverIndex) => {
        let currentY = 10

        doc.text(`${replaceSerbianChars(this.$t('tables.Driver'))}: ${replaceSerbianChars(driver.driverName)}`, 10, currentY)
        currentY += 8
        const selectedDate = new Date(this.searchObject.selectedDate)
        doc.text(
          `${this.$t('reportHeaders.Date From')}: ${selectedDate.getDate()}.${selectedDate.getMonth() + 1}.${selectedDate.getFullYear()} 00:00:00`,
          10,
          currentY
        )
        currentY += 8
        const periodForCheck = this.periods.find(
          (x) => x.value === this.searchObject.selectedPeriodForCheck
        ).text
        doc.text(
          `${this.$t('periodForCheck')}: ${replaceSerbianChars(periodForCheck)}`,
          10,
          currentY
        )
        currentY += 8

        if (driver.violations.length === 0) {
          doc.text(`${this.$t('mapFilters.No data available')}`, 10, currentY)
          currentY += 8
        } else {
          doc.text(
            `${this.$t('timeSpentOnDriving')}: ${driver.totalTimeSpentOnDriving}`,
            10,
            currentY
          )
          currentY += 8
          doc.text(
            `${this.$t('timeSpentOnPause')}: ${driver.totalTimeSpentOnPause}`,
            10,
            currentY
          )
          currentY += 10

          const headers = [
            this.$t("date"),
            this.$t("violation"),
          ]

          doc.autoTable({
            startY: currentY,
            head: [headers.map(replaceSerbianChars)],
            body: driver.violations.map((violation) => [
              violation.timestamp,
              replaceSerbianChars(violation.violation),
            ]),
            styles: {
              font: "helvetica",
              fontSize: 10,
              halign: "center",
              textColor: [0, 0, 0],
              fillColor: [255, 255, 255],
            },
            bodyStyles: {
              fillColor: [255, 255, 255],
            },
            headStyles: {
              fillColor: [230, 230, 230],
              textColor: [0, 0, 0],
            },
            didParseCell: (data) => {
              if (data.section === "body") {
                const rowIndex = data.row.index
                const violation = driver.violations[rowIndex]
                data.cell.styles.textColor = violation.isMinorViolation
                  ? [255, 165, 0]
                  : [255, 0, 0]
              }
            },
            margin: { top: 10 },
          })
        }

        if (driverIndex < this.response.drivers.length - 1) {
          doc.addPage()
        }
      })

      return doc.output("blob")
    },
    async exportAsPdf() {
      const zip = new JSZip()
      const resultsBlob = this.generateResultsPDF()
      const violationsBlob = this.generateViolationsPDF()

      zip.file("Rezultati.pdf", resultsBlob)
      zip.file("Prekrsaji.pdf", violationsBlob)

      var currentDate = new Date()
      var zipFileName = `ZOBS-${currentDate.getDate()}.${currentDate.getMonth() + 1}.${currentDate.getFullYear()} ${currentDate.getHours()}_${currentDate.getMinutes()}_${currentDate.getSeconds()}.zip`

      const a = document.createElement("a")
      a.href = URL.createObjectURL(await zip.generateAsync({ type: "blob" }))
      a.download = zipFileName
      a.style.display = "none"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    addPageIfNeeded(doc, y) {
      if (y > 190) {
        doc.addPage()
        return 10 
      }
      return y
    }
  },
};
</script>

<style>
.elevation-1 {
  margin-top: 20px;
}
.results-data-container {
  height: 60vh;
  overflow-y: auto;
}
.green-row {
  background-color: #A5D6A7;
}
.red-row {
  background-color: #EF9A9A;
}
</style>
